/**
 * Scene Manager —— 只负责空间。
 * 对应技术设计 §4.2。原文约束：「Scene 不知道『氧气实验』具体规则，只负责空间」（`:240`）。
 *
 * 所以本文件里不允许出现任何具体实验的名字或 id：输入只有 SceneSpec 与器材列表。
 */

import type { EquipmentSpec, SceneSpec } from "../data/schemas.js";
import type { LabObject } from "../eom/lab-object.js";
import { instantiate } from "../eom/equipment-registry.js";
import { layoutObjects, type LayoutInput } from "./layout-engine.js";
import { getLayout, selectZones, type StageSize, type Zone } from "./zones.js";

export interface Scene {
  layout: string;
  stage: StageSize;
  zones: Zone[];
}

export class SceneManager {
  private scene: Scene | null = null;

  /** 由 SceneSpec 建出场景空间。版式或分区不认识时直接抛错，不静默降级。 */
  build(spec: SceneSpec): Scene {
    const { stage } = getLayout(spec.layout);
    this.scene = {
      layout: spec.layout,
      stage,
      zones: selectZones(spec.layout, spec.zones),
    };
    return this.scene;
  }

  getScene(): Scene | null {
    return this.scene;
  }

  /**
   * 由 EquipmentSpec 实例化器材并完成初始排布。
   * 器材类型未知时 instantiate 会抛错 —— 但正常路径下 validators 已经先拦过一次。
   */
  placeEquipment(specs: EquipmentSpec[]): LabObject[] {
    const scene = this.scene;
    if (!scene) {
      throw new Error("SceneManager.placeEquipment 必须在 build() 之后调用");
    }

    const inputs: LayoutInput[] = specs.map((spec) => {
      const object = instantiate({
        id: spec.id,
        type: spec.type,
        ...(spec.name !== undefined ? { name: spec.name } : {}),
        ...(spec.substanceId !== undefined ? { substanceId: spec.substanceId } : {}),
        ...(spec.pose !== undefined ? { pose: spec.pose } : {}),
        ...(spec.state !== undefined ? { state: spec.state } : {}),
      });
      return {
        object,
        ...(spec.zone !== undefined ? { zone: spec.zone } : {}),
        hasExplicitPose: spec.pose?.x !== undefined && spec.pose?.y !== undefined,
      };
    });

    return layoutObjects(inputs, scene.zones);
  }

  dispose(): void {
    this.scene = null;
  }
}
